import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, LayoutDashboard, CreditCard, Building, Users, Ticket, FileText, Menu, X, Globe, ShieldCheck, Mail, FolderKanban, Lock } from 'lucide-react';
import { Button } from './ui/button';

function DemoLayout({ children, title }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/demo' },
    { name: 'My Subscription', icon: CreditCard, path: '/demo/subscription' },
    { name: 'Billing', icon: CreditCard, path: '/demo/billing' },
    { name: 'Security Status', icon: Shield, path: '/demo/security-status' },
    { name: 'Dark Web Monitoring', icon: Globe, path: '/demo/dark-web' },
    { name: 'Vulnerability Scan', icon: ShieldCheck, path: '/demo/vulnerability' },
    { name: 'Email Security', icon: Mail, path: '/demo/email-security' },
    { name: 'Company Details', icon: Building, path: '/demo/company' },
    { name: 'Contacts', icon: Users, path: '/demo/contacts' },
    { name: 'Support', icon: Ticket, path: '/demo/support' },
    { name: 'Projects', icon: FolderKanban, path: '/demo/projects' },
    { name: 'Resources', icon: FileText, path: '/demo/resources' },
  ];

  const demoUser = {
    name: "Demo User",
    company: "Acme Corporation"
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Demo Banner */}
      <div className="fixed top-0 left-0 right-0 bg-secondary/20 border-b border-secondary/30 px-4 py-2 z-50">
        <div className="flex items-center justify-between max-w-full">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-secondary" />
            <span className="text-sm font-mono text-secondary">DEMO MODE - Read Only Preview</span>
          </div>
          <a 
            href="/login" 
            className="text-sm font-mono text-primary hover:underline"
          >
            Login to access your portal →
          </a>
        </div>
      </div>

      {/* Sidebar - Desktop */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col border-r border-border bg-card/50 backdrop-blur-xl pt-10">
        <div className="flex h-16 items-center gap-2 border-b border-border px-6">
          <div className="flex items-center">
            <span className="text-2xl font-bold" style={{ color: '#00F8B3' }}>x</span>
            <span className="text-2xl font-bold text-white">entra</span>
          </div>
        </div>
        
        <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-mono rounded-md transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary border border-primary/20'
                    : 'text-muted-foreground hover:bg-accent/10 hover:text-accent'
                }`}
              >
                <Icon className="h-4 w-4" />
                {item.name}
              </button>
            );
          })}
        </nav>
        
        <div className="border-t border-border p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
              <span className="text-primary font-mono font-bold">D</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-mono font-medium truncate">{demoUser.name}</p>
              <p className="text-xs text-muted-foreground truncate">{demoUser.company}</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/login')}
            variant="outline"
            className="w-full justify-start"
          >
            <Lock className="h-4 w-4 mr-2" />
            Login to Your Portal
          </Button>
        </div>
      </aside>

      {/* Sidebar - Mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setSidebarOpen(false)}></div>
          <aside className="fixed inset-y-0 left-0 w-64 bg-card border-r border-border pt-10">
            <div className="flex h-16 items-center justify-between border-b border-border px-6">
              <div className="flex items-center">
                <span className="text-2xl font-bold" style={{ color: '#00F8B3' }}>x</span>
                <span className="text-2xl font-bold text-white">entra</span>
              </div>
              <button onClick={() => setSidebarOpen(false)}>
                <X className="h-6 w-6 text-muted-foreground" />
              </button>
            </div>
            
            <nav className="flex-1 space-y-1 px-3 py-4">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-mono rounded-md transition-colors ${
                      isActive
                        ? 'bg-primary/10 text-primary border border-primary/20'
                        : 'text-muted-foreground hover:bg-accent/10 hover:text-accent'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {item.name}
                  </button>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="lg:pl-64 pt-10">
        <header className="sticky top-10 z-10 flex h-16 items-center gap-4 border-b border-border bg-card/50 backdrop-blur-xl px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-muted-foreground"
          >
            <Menu className="h-6 w-6" />
          </button>
          <h1 className="text-xl font-mono font-bold">{title}</h1>
        </header>
        
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
}

export default DemoLayout;
